import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { errMsg, fmtMinutes } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ImagePlus, Mail, MessageCircle, Play, Plus, Send } from "lucide-react";

const STATUS_STYLES = {
  open: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  resolved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  closed: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};

export default function TicketDetail() {
  const { id } = useParams();
  const [ticket, setTicket] = useState(null);
  const [agents, setAgents] = useState([]);
  const [timer, setTimer] = useState(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: "", description: "", priority: "medium", assignee_id: "" });
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [noteFile, setNoteFile] = useState(null);
  const [tab, setTab] = useState("tasks");
  const [corresp, setCorresp] = useState([]);
  const [channel, setChannel] = useState("email");
  const [replyText, setReplyText] = useState("");
  const [phone, setPhone] = useState("");

  const load = async () => {
    try {
      const [t, a, tm, n, c] = await Promise.all([
        api.get(`/tickets/${id}`),
        api.get("/agents"),
        api.get("/timer/active"),
        api.get(`/tickets/${id}/notes`),
        api.get(`/tickets/${id}/correspondence`),
      ]);
      setTicket(t.data);
      setAgents(a.data);
      setTimer(tm.data.timer);
      setNotes(n.data);
      setCorresp(c.data);
      setPhone(t.data.requester_phone || "");
      setChannel(t.data.requester_email ? "email" : "whatsapp");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const patch = async (data) => {
    try {
      const r = await api.patch(`/tickets/${id}`, data);
      setTicket((prev) => ({ ...prev, ...r.data }));
      toast.success("Ticket updated");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const createTask = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tasks", { ...taskForm, ticket_id: id, assignee_id: taskForm.assignee_id || null });
      toast.success("Task added to board");
      setShowTaskForm(false);
      setTaskForm({ title: "", description: "", priority: "medium", assignee_id: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const startTimer = async (taskId) => {
    try {
      await api.post("/timer/start", { task_id: taskId });
      toast.success("Timer started");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const fileUrl = (path) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}?auth=${encodeURIComponent(localStorage.getItem("cd_token") || "")}`;

  const addNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim() && !noteFile) return toast.error("Add a note or a screenshot");
    try {
      const fd = new FormData();
      fd.append("text", noteText);
      if (noteFile) fd.append("screenshot", noteFile);
      await api.post(`/tickets/${id}/notes`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Note added");
      setNoteText("");
      setNoteFile(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const savePhone = async () => {
    try {
      await api.patch(`/tickets/${id}`, { requester_phone: phone.trim() });
      toast.success("Phone number saved");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    try {
      const r = await api.post(`/tickets/${id}/reply`, { channel, body: replyText });
      toast.success(
        channel === "whatsapp" && r.data.status === "simulated"
          ? "WhatsApp reply saved — SIMULATED (provider not connected yet)"
          : "Reply sent"
      );
      setReplyText("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (!ticket) return <p className="text-muted-foreground font-mono-digits text-sm animate-pulse">Loading…</p>;

  const agentName = (aid) => agents.find((a) => a.id === aid)?.name || "Unassigned";

  return (
    <div className="space-y-8" data-testid="ticket-detail">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to tickets
      </Link>

      <div className="rounded-lg border border-border bg-card p-6 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="font-mono-digits text-xs text-muted-foreground tracking-widest">{ticket.number}</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mt-1">{ticket.subject}</h1>
          </div>
          <div className="flex items-center gap-2">
            {(ticket.source === "email" || ticket.source === "gmail" || ticket.source === "whatsapp") && (
              <span className={`flex items-center gap-1.5 text-[11px] font-mono-digits uppercase tracking-wider rounded px-2 py-1 border ${
                ticket.source === "whatsapp"
                  ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"
                  : "text-sky-400 border-sky-500/30 bg-sky-500/10"
              }`}>
                {ticket.source === "whatsapp" ? <MessageCircle className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
                via {ticket.source === "whatsapp" ? "whatsapp" : "email"}
              </span>
            )}
            <span className={`px-2 py-1 rounded border text-[11px] font-mono-digits uppercase tracking-wider ${STATUS_STYLES[ticket.status]}`}>
              {ticket.status}
            </span>
          </div>
        </div>

        <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{ticket.description || "No description."}</p>

        <div className="flex items-center gap-3 flex-wrap text-sm border-t border-border/60 pt-3">
          <span className="font-mono-digits text-[10px] uppercase tracking-widest text-muted-foreground">Contact</span>
          <span data-testid="requester-email" className="text-muted-foreground">{ticket.requester_email || "no email"}</span>
          <div className="flex items-center gap-2">
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="WhatsApp number (+27…)"
              data-testid="requester-phone-input"
              className="rounded-md bg-secondary/60 border border-border px-3 py-1.5 text-sm outline-none focus:border-orange-500/60 w-48" />
            <button onClick={savePhone} data-testid="requester-phone-save"
              className="rounded-md border border-orange-500/40 text-orange-400 hover:bg-orange-500/10 text-xs font-semibold px-3 py-1.5 transition-colors">
              Save
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 pt-2">
          <div>
            <label className="font-mono-digits text-[10px] uppercase tracking-widest text-muted-foreground">Status</label>
            <select value={ticket.status} onChange={(e) => patch({ status: e.target.value })} data-testid="ticket-status-select"
              className="mt-1 w-full rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60">
              {["open", "pending", "resolved", "closed"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="font-mono-digits text-[10px] uppercase tracking-widest text-muted-foreground">Priority</label>
            <select value={ticket.priority} onChange={(e) => patch({ priority: e.target.value })} data-testid="ticket-detail-priority-select"
              className="mt-1 w-full rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60">
              {["low", "medium", "high", "urgent"].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="font-mono-digits text-[10px] uppercase tracking-widest text-muted-foreground">Assignee</label>
            <select value={ticket.assignee_id || ""} onChange={(e) => patch({ assignee_id: e.target.value || null })} data-testid="ticket-detail-assignee-select"
              className="mt-1 w-full rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60">
              <option value="">Unassigned</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="flex gap-1 border-b border-border" data-testid="ticket-tabs">
        {[
          ["tasks", "Tasks", "tab-tasks"],
          ["notes", "Notes", "tab-notes"],
          ["correspondence", "Correspondence", "tab-correspondence"],
        ].map(([key, label, testid]) => (
          <button key={key} data-testid={testid} onClick={() => setTab(key)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === key ? "border-orange-500 text-orange-400" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "tasks" && (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Tasks</h2>
          <button data-testid="create-task-from-ticket-button" onClick={() => setShowTaskForm(!showTaskForm)}
            className="flex items-center gap-2 rounded-md bg-orange-500 hover:bg-orange-600 active:scale-[0.98] text-white text-sm font-semibold px-4 py-2 transition-all">
            <Plus className="h-4 w-4" /> Create task
          </button>
        </div>

        {showTaskForm && (
          <form data-testid="create-task-form" onSubmit={createTask} className="rounded-lg border border-orange-500/30 bg-card p-5 space-y-4">
            <div className="grid sm:grid-cols-3 gap-4">
              <input required placeholder="Task title" value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                data-testid="task-title-input"
                className="rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60" />
              <select value={taskForm.priority} onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                data-testid="task-priority-select"
                className="rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60">
                {["low", "medium", "high", "urgent"].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <select value={taskForm.assignee_id} onChange={(e) => setTaskForm({ ...taskForm, assignee_id: e.target.value })}
                data-testid="task-assignee-select"
                className="rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60">
                <option value="">Unassigned</option>
                {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <button type="submit" data-testid="task-submit-button"
              className="rounded-md bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-2 transition-colors">
              Add task
            </button>
          </form>
        )}

        <div className="space-y-2">
          {ticket.tasks?.length === 0 && (
            <p className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center">
              No tasks yet — create one and it will appear on the Kanban board.
            </p>
          )}
          {ticket.tasks?.map((t) => (
            <div key={t.id} data-testid={`task-row-${t.id}`}
              className="flex items-center gap-4 rounded-lg border border-border bg-card px-4 py-3 hover:border-orange-500/40 transition-colors">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold truncate">{t.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {agentName(t.assignee_id)} · <span className="font-mono-digits">{fmtMinutes(t.logged_minutes || 0)}</span> logged
                </p>
              </div>
              <span className={`px-2 py-0.5 rounded border text-[10px] font-mono-digits uppercase tracking-wider ${
                t.status === "done" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                : t.status === "in_progress" ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
                : "bg-slate-500/15 text-slate-400 border-slate-500/30"
              }`}>{t.status.replace("_", " ")}</span>
              <button data-testid={`start-timer-${t.id}`} onClick={() => startTimer(t.id)}
                disabled={timer?.task_id === t.id}
                className="flex items-center gap-1.5 rounded-md border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 text-xs font-semibold px-3 py-1.5 transition-colors disabled:opacity-40">
                <Play className="h-3 w-3" /> {timer?.task_id === t.id ? "Running" : "Timer"}
              </button>
            </div>
          ))}
        </div>
      </div>
      )}

      {tab === "notes" && (
      <div className="space-y-4">
        <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Notes & Screenshots</h2>
        <form data-testid="add-note-form" onSubmit={addNote} className="rounded-lg border border-border bg-card p-5 space-y-3">
          <textarea rows={2} placeholder="Add a note…" value={noteText} onChange={(e) => setNoteText(e.target.value)}
            data-testid="note-text-input"
            className="w-full rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60" />
          <div className="flex items-center gap-3 flex-wrap">
            <label data-testid="note-screenshot-label"
              className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground hover:border-orange-500/40 hover:text-foreground cursor-pointer transition-colors">
              <ImagePlus className="h-4 w-4" />
              {noteFile ? noteFile.name : "Attach screenshot"}
              <input type="file" accept="image/*" className="hidden" data-testid="note-screenshot-input"
                onChange={(e) => setNoteFile(e.target.files?.[0] || null)} />
            </label>
            <button type="submit" data-testid="note-submit-button"
              className="rounded-md bg-orange-500 hover:bg-orange-600 active:scale-[0.98] text-white text-sm font-semibold px-4 py-2 transition-all">
              Add note
            </button>
          </div>
        </form>
        <div className="space-y-2">
          {notes.length === 0 && (
            <p className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center">No notes yet.</p>
          )}
          {notes.map((n) => (
            <div key={n.id} data-testid={`note-${n.id}`} className="rounded-lg border border-border bg-card px-4 py-3.5">
              <p className="text-xs text-muted-foreground">
                <span className="text-foreground font-semibold">{n.user_name}</span> · {new Date(n.created_at).toLocaleString()}
              </p>
              {n.text && <p className="text-sm mt-1.5 whitespace-pre-wrap">{n.text}</p>}
              {n.attachment_path && (
                <a href={fileUrl(n.attachment_path)} target="_blank" rel="noreferrer">
                  <img src={fileUrl(n.attachment_path)} alt={n.attachment_name || "screenshot"}
                    className="mt-3 max-h-64 rounded-md border border-border hover:border-orange-500/40 transition-colors" />
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
      )}

      {tab === "correspondence" && (
        <div className="space-y-4" data-testid="correspondence-tab">
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Correspondence</h2>

          <div className="space-y-2">
            {corresp.length === 0 && (
              <p className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center">
                No messages yet — inbound emails and WhatsApp messages will appear here.
              </p>
            )}
            {corresp.map((m) => (
              <div key={m.id} data-testid={`message-${m.id}`}
                className={`rounded-lg border px-4 py-3 ${
                  m.direction === "outbound" ? "border-orange-500/30 bg-orange-500/5 ml-4 sm:ml-10" : "border-border bg-card mr-4 sm:mr-10"
                }`}>
                <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                  {m.channel === "whatsapp"
                    ? <MessageCircle className="h-3.5 w-3.5 text-emerald-400" />
                    : <Mail className="h-3.5 w-3.5 text-sky-400" />}
                  <span className="font-mono-digits uppercase tracking-wider">{m.channel}</span>
                  <span>·</span>
                  <span className="text-foreground font-semibold">
                    {m.direction === "outbound" ? m.user_name : (m.from || "client")}
                  </span>
                  <span>·</span>
                  <span>{new Date(m.created_at).toLocaleString()}</span>
                  {m.status === "simulated" && (
                    <span className="font-mono-digits text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30">
                      Simulated
                    </span>
                  )}
                </div>
                <p className="text-sm mt-1.5 whitespace-pre-wrap">{m.body}</p>
              </div>
            ))}
          </div>

          <form data-testid="reply-form" onSubmit={sendReply} className="rounded-lg border border-border bg-card p-5 space-y-3">
            <div className="flex gap-2">
              <button type="button" data-testid="channel-email-button"
                onClick={() => setChannel("email")} disabled={!ticket.requester_email}
                title={ticket.requester_email ? `Send to ${ticket.requester_email}` : "No email address on this ticket"}
                className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold border transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                  channel === "email" ? "bg-sky-500/15 text-sky-400 border-sky-500/40" : "border-border text-muted-foreground hover:text-foreground"
                }`}>
                <Mail className="h-4 w-4" /> Email
              </button>
              <button type="button" data-testid="channel-whatsapp-button"
                onClick={() => setChannel("whatsapp")} disabled={!ticket.requester_phone}
                title={ticket.requester_phone ? `WhatsApp ${ticket.requester_phone}` : "No phone number on this ticket"}
                className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold border transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                  channel === "whatsapp" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" : "border-border text-muted-foreground hover:text-foreground"
                }`}>
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </button>
            </div>
            <textarea rows={3} value={replyText} onChange={(e) => setReplyText(e.target.value)}
              placeholder={channel === "email" ? "Write an email reply…" : "Write a WhatsApp message…"}
              data-testid="reply-body-input"
              className="w-full rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60" />
            <button type="submit" data-testid="reply-send-button"
              className="flex items-center gap-2 rounded-md bg-orange-500 hover:bg-orange-600 active:scale-[0.98] text-white text-sm font-semibold px-4 py-2 transition-all">
              <Send className="h-4 w-4" /> Send {channel === "email" ? "email" : "WhatsApp"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
